import type { ComponentConfig } from '@measured/puck';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';

export interface CTAProps {
  title: string;
  description?: string;
  ctaLabel: string;
  ctaHref: string;
  variant?: 'brand' | 'accent' | 'dark';
}

// Tokens de surface par variant (couleurs, contraste).
// Aligné avec DESIGN-SYSTEM.md (tokens Tailwind, pas de hex en dur).
const SURFACES = {
  brand: {
    section: 'bg-brand-700 text-ivory-50',
    title: 'on-dark',
    description: 'text-ivory-50/85',
    button: 'accent' as const,
  },
  accent: {
    section: 'bg-accent-500 text-ink',
    title: '',
    description: 'text-ink/80',
    button: 'primary' as const,
  },
  dark: {
    section: 'bg-ink text-ivory-50',
    title: 'on-dark',
    description: 'text-ivory-50/75',
    button: 'accent' as const,
  },
};

export function CTA({ title, description, ctaLabel, ctaHref, variant = 'brand' }: CTAProps) {
  const surface = SURFACES[variant];
  return (
    <section className={`${surface.section} py-16 md:py-20`}>
      <Container className="flex flex-col items-start justify-between gap-6 text-balance md:flex-row md:items-center">
        <div className="max-w-2xl">
          <h2 className={`h-section ${surface.title}`.trim()}>{title}</h2>
          {description ? (
            <p className={`mt-3 text-body leading-relaxed ${surface.description}`}>{description}</p>
          ) : null}
        </div>
        <Button asChild size="lg" variant={surface.button}>
          <a href={ctaHref}>{ctaLabel}</a>
        </Button>
      </Container>
    </section>
  );
}

export const CTAConfig: ComponentConfig<CTAProps> = {
  fields: {
    title: { type: 'textarea' },
    description: { type: 'textarea' },
    ctaLabel: { type: 'text' },
    ctaHref: { type: 'text' },
    variant: {
      type: 'select',
      options: [
        { label: 'Brand (émeraude)', value: 'brand' },
        { label: 'Accent (lime)', value: 'accent' },
        { label: 'Sombre (ink)', value: 'dark' },
      ],
    },
  },
  defaultProps: {
    title: 'Prêt à estimer vos aides ?',
    description: 'Diagnostic 60 sec, gratuit, sans engagement.',
    ctaLabel: 'Démarrer',
    ctaHref: '/outils',
    variant: 'brand',
  },
  render: CTA,
};
